/**
 * useNotifications Hook
 *
 * Custom React hook for managing notifications via REST API.
 * Used for features that don't require real-time WebSocket updates.
 *
 * Features:
 * - Fetch paginated notifications
 * - Mark notifications as read
 * - Delete notifications
 * - Filter by type, urgency, read status
 * - Loading and error states
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * const { notifications, loading, fetchNotifications } = useNotifications();
 */

import { useState, useCallback } from 'react';
import apiRequest from '../../services/api';

/**
 * Notifications REST API hook
 *
 * @returns {Object} Notification state and methods
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================================
  // FETCH NOTIFICATIONS
  // ============================================================================

  /**
   * Fetch notifications with filters
   * @param {Object} filters - Filter options
   * @param {number} filters.page - Page number (default: 1)
   * @param {number} filters.limit - Items per page (default: 20)
   * @param {boolean} filters.unreadOnly - Show only unread (default: false)
   * @param {string} filters.type - Filter by type
   * @param {string} filters.urgency - Filter by urgency
   */
  const fetchNotifications = useCallback(
    async (filters = {}) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (filters.page) {params.append('page', filters.page);}
        if (filters.limit) {params.append('limit', filters.limit);}
        if (filters.unreadOnly) {params.append('unreadOnly', 'true');}
        if (filters.type) {params.append('type', filters.type);}
        if (filters.urgency) {params.append('urgency', filters.urgency);}

        const queryString = params.toString();
        const url = `/api/v1/notifications${queryString ? `?${queryString}` : ''}`;

        const data = await apiRequest(url, { method: 'GET' });

        setNotifications(data.notifications);
        setPagination(data.pagination);
      } catch (err) {
        console.error('[useNotifications] Failed to fetch notifications:', err);
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to fetch notifications');
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // GET NOTIFICATION BY ID
  // ============================================================================

  /**
   * Get single notification by ID
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} Notification object
   */
  const getNotificationById = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);

      try {
        const data = await apiRequest(`/api/v1/notifications/${id}`, { method: 'GET' });
        return data.notification;
      } catch (err) {
        console.error('[useNotifications] Failed to get notification:', err);
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to get notification');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ============================================================================
  // GET UNREAD COUNT
  // ============================================================================

  /**
   * Get unread notification count
   * @returns {Promise<number>} Unread count
   */
  const getUnreadCount = useCallback(async () => {
    try {
      const data = await apiRequest('/api/v1/notifications/unread-count', { method: 'GET' });
      return data.count;
    } catch (err) {
      console.error('[useNotifications] Failed to get unread count:', err);
      return 0;
    }
  }, []);

  // ============================================================================
  // MARK AS READ
  // ============================================================================

  /**
   * Mark notification as read
   * @param {string} id - Notification ID
   */
  const markAsRead = useCallback(
    async (id) => {
      try {
        await apiRequest(`/api/v1/notifications/${id}/read`, {
          method: 'PUT',
        });

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date() } : n))
        );
      } catch (err) {
        console.error('[useNotifications] Failed to mark as read:', err);
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to mark as read');
        }
      }
    },
    []
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await apiRequest('/api/v1/notifications/mark-all-read', {
        method: 'PUT',
      });

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date() })));
    } catch (err) {
      console.error('[useNotifications] Failed to mark all as read:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to mark all as read');
      }
    }
  }, []);

  // ============================================================================
  // DELETE NOTIFICATION
  // ============================================================================

  /**
   * Delete notification
   * @param {string} id - Notification ID
   */
  const deleteNotification = useCallback(
    async (id) => {
      try {
        await apiRequest(`/api/v1/notifications/${id}`, {
          method: 'DELETE',
        });

        // Update local state
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      } catch (err) {
        console.error('[useNotifications] Failed to delete notification:', err);
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to delete notification');
        }
      }
    },
    []
  );

  // ============================================================================
  // SEND TEST NOTIFICATION (ADMIN ONLY)
  // ============================================================================

  /**
   * Send test notification (admin only)
   * @param {Object} notification - Notification data
   */
  const sendTestNotification = useCallback(
    async (notification) => {
      try {
        const data = await apiRequest('/api/v1/notifications/test', {
          method: 'POST',
          body: JSON.stringify(notification),
        });

        return data.notification;
      } catch (err) {
        console.error('[useNotifications] Failed to send test notification:', err);
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to send test notification');
        }
        return null;
      }
    },
    []
  );

  // ============================================================================
  // RETURN HOOK INTERFACE
  // ============================================================================

  return {
    // Data
    notifications,
    pagination,
    loading,
    error,

    // Methods
    fetchNotifications,
    getNotificationById,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    sendTestNotification,
  };
}
